import { LockIcon } from 'lucide-react';

import { FulfillmentStrategy, SkuType } from '@invenco/products-interface';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/core/tooltip';
import { SkuBadge } from '@/pages/seller/components/sku-badge';

import { isBundleComponentLine, isOrderLineManaged, type OrderLineRow } from './order-line-row';

// viewBox is 20x40: the stem sits at x=10 and the branch curves out to the SKU
// content (x=18) at the row's vertical mid-point (y~=16-20).
const BUNDLE_CONNECTOR_ELBOW_PATH = 'M10 0 V16 C10 18 12 20 18 20'; // last component
const BUNDLE_CONNECTOR_TEE_PATH = 'M10 0 V40 M10 16 C10 18 12 20 18 20'; // mid component

type SkuDescriptionCellProps = {
  line: OrderLineRow;
  isLastComponent: boolean;
  reserveTypeIconSpace?: boolean;
};

function BundleComponentConnector({ isLast }: { isLast: boolean }) {
  return (
    <span className="absolute top-0 left-4 h-full w-5 text-border" aria-hidden>
      <svg className="absolute inset-0 size-full overflow-visible" viewBox="0 0 20 40" fill="none">
        <path
          d={isLast ? BUNDLE_CONNECTOR_ELBOW_PATH : BUNDLE_CONNECTOR_TEE_PATH}
          stroke="currentColor"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
}

function BundleComponentSkuDescription({
  line,
  isLastComponent,
}: Pick<SkuDescriptionCellProps, 'line' | 'isLastComponent'>) {
  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <span className="sr-only">Bundle component</span>
      <BundleComponentConnector isLast={isLastComponent} />
      <span className="w-5 shrink-0" aria-hidden />
      <span className="w-7 shrink-0 text-right">{line.qtyOrdered} x</span>
      <SkuBadge externalSkuName={line.externalSkuName} name={line.skuName} />
      <span className="truncate">{line.description}</span>
    </div>
  );
}

export function SkuDescriptionCell({
  line,
  isLastComponent,
  reserveTypeIconSpace,
}: SkuDescriptionCellProps) {
  if (isBundleComponentLine(line)) {
    return <BundleComponentSkuDescription line={line} isLastComponent={isLastComponent} />;
  }

  return (
    <div className="flex min-w-0 items-center gap-2">
      {isOrderLineManaged(line) && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              aria-label="Managed by Shopify"
              className="inline-flex size-4 shrink-0 items-center justify-center text-primary"
            >
              <LockIcon className="size-3.5" aria-hidden />
            </span>
          </TooltipTrigger>
          <TooltipContent>Managed by Shopify</TooltipContent>
        </Tooltip>
      )}
      <SkuBadge
        externalSkuName={line.externalSkuName}
        name={line.skuName}
        type={line.fulfillmentStrategy === FulfillmentStrategy.BUNDLE ? SkuType.BUNDLE : undefined}
        reserveTypeIconSpace={reserveTypeIconSpace}
      />
      <span className="truncate">{line.description}</span>
    </div>
  );
}
